/**
 * Context Builder
 *
 * Gathers application context for AI assistant prompts.
 * Uses the store registry to snapshot all active CDSE store states, and
 * the active i18next language for the user's locale.
 */

import { buildContextSnapshot } from '@commandDrivenStateEngine/core/storeRegistry';
import i18n from '../../translation/i18n';

export type AppLocale = 'en' | 'es';

export interface AppContext {
  route: string;
  storeStates: Record<string, unknown>;
  /** User's UI language. Backend agent prompt + replies pivot on this. */
  locale: AppLocale;
}

/**
 * Coerce an i18n language code (e.g. `'es-AR'`, `'en'`, `'es'`) into the
 * backend's supported `AssistantLocale` union. Falls back to `'es'` when the
 * locale is unrecognized — matches the legacy default and avoids forcing the
 * backend to widen its enum for a transient i18n hiccup.
 */
const resolveLocale = ( raw: string | undefined ): AppLocale => {
  const lang = ( raw ?? '' ).toLowerCase().split( '-' )[ 0 ];
  return lang === 'en' ? 'en' : 'es';
};

export function buildAppContext(): AppContext {
  return {
    route: window.location.pathname,
    storeStates: buildContextSnapshot(),
    locale: resolveLocale( i18n.language )
  };
}
